import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { MyFromTextarea } from "@/components/zthd/MyFormField";
import { MarkdownTextarea } from "@/components/markdown-textarea";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { mutateRequest, postFetcher } from "@/lib/swr";
import { useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { z } from "zod";
import UserSelector, { type PickerUser } from "@/components/user-selector";
import type { Engagement, Version } from "@/types/domain";

const validationSchema = z.object({
  summary: z
    .string()
    .min(10, "Summary is required and must be at least 10 characters long")
    // Issue titles cap at 255 (prd.docName); put long repro steps in details
    .max(255, "Keep the summary under 255 characters; use details below"),
});

// Bug submission, recorded in-app (no Feishu task). Three scopes by props:
//  - pid only: app-level bug
//  - version: bug of that version
//  - engagement (+version): bug of a PRD within that version
const SubmitBugs = ({
  pid,
  version,
  engagement,
  className,
}: {
  pid?: string;
  version?: Version;
  engagement?: Engagement;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  // Owner candidates are the app's visible members (any role), same as PRD members.
  const appId = pid ?? version?.pid ?? engagement?.appId;
  // Optional assign-on-create: the picked owner gets @-ed in the group card
  // and a 1:1 ping, same as assigning later from the bug detail.
  const [owner, setOwner] = useState<PickerUser | null>(null);
  // Optional markdown detail (repro steps, screenshots via paste/drop) —
  // lands as the new bug's body.
  const [detail, setDetail] = useState("");
  // Issue unification: bugs are prds with type=bug (provenance in source*).
  const { trigger, isMutating } = useSWRMutation("/prds", postFetcher, {
    onSuccess: () => {
      form.reset();
      setOwner(null);
      setDetail("");
      mutateRequest("/prds");
      setOpen(false);
    },
  });

  const defaultValues = {
    summary: "",
  };

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const handleSubmit = (data: z.infer<typeof validationSchema>) => {
    if (isMutating) return;
    trigger({
      type: "bug",
      title: data.summary,
      appId: pid,
      sourceVersionId: version?.id,
      sourceEngagementId: engagement ? engagement.id : undefined,
      ownerId: owner?.id,
      content: detail.trim() || undefined,
    });
  };

  const scope = engagement
    ? `PRD: ${engagement.prd?.docName ?? engagement.id}`
    : version
      ? `Version: ${version.docName}`
      : "Scope: whole app";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("text-destructive", className)}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Bugs
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit bugs</DialogTitle>
          <DialogDescription>
            Please submit the bugs you found.
          </DialogDescription>
        </DialogHeader>
        <p>{scope}</p>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <MyFromTextarea
              name="summary"
              form={form}
              placeholder="write something."
              rows={3}
            />
            <MarkdownTextarea
              value={detail}
              onValueChange={setDetail}
              withPreview
              placeholder="Details (optional, markdown; paste/drop screenshots to upload)"
              rows={4}
              className="font-mono text-xs leading-relaxed"
            />
            <div className="flex items-center justify-between">
              <UserSelector
                selectedUser={owner}
                onContinue={setOwner}
                title="Assign owner"
                description="Optional: who follows up on this bug."
                sourceUrl={appId ? `/apps/${appId}/members` : undefined}
                hint={
                  appId
                    ? "Only members of this app are listed — ask the app owner to add anyone missing."
                    : undefined
                }
                trigger={
                  <Button type="button" variant="outline" size="sm">
                    {owner ? `Owner: ${owner.name}` : "Assign owner (optional)"}
                  </Button>
                }
              />
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {isMutating ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitBugs;
